import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { IProviderPayableLineListItem } from 'features/salesOrder/types';
import { getAllProviderPayableLineListBySaleOrderId } from 'features/salesOrder/services';

interface ProviderPayablesContextValue {
	lines: IProviderPayableLineListItem[];
	loading: boolean;
	error: string | null;
	refresh: () => Promise<IProviderPayableLineListItem[]>;
	getLinesForService: (serviceId: string) => IProviderPayableLineListItem[];
}

const ProviderPayablesContext = createContext<ProviderPayablesContextValue | null>(null);

interface ProviderPayablesProviderProps {
	saleOrderId: string;
	refreshKey?: number;
	children: React.ReactNode;
}

export const ProviderPayablesProvider = ({
	saleOrderId,
	refreshKey = 0,
	children,
}: ProviderPayablesProviderProps): JSX.Element => {
	const [lines, setLines] = useState<IProviderPayableLineListItem[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const refresh = useCallback(async (): Promise<IProviderPayableLineListItem[]> => {
		if (!saleOrderId) {
			setLines([]);
			return [];
		}

		try {
			setLoading(true);
			setError(null);
			const res = await getAllProviderPayableLineListBySaleOrderId(saleOrderId);
			setLines(res.data);
			return res.data;
		} catch {
			setError('No se pudieron cargar las órdenes de compra.');
			setLines([]);
			return [];
		} finally {
			setLoading(false);
		}
	}, [saleOrderId]);

	useEffect(() => {
		refresh();
	}, [refresh, refreshKey]);

	const getLinesForService = useCallback(
		(serviceId: string) => lines.filter((line) => line.serviceId === serviceId),
		[lines],
	);

	const value = useMemo(
		() => ({
			lines,
			loading,
			error,
			refresh,
			getLinesForService,
		}),
		[lines, loading, error, refresh, getLinesForService],
	);

	return <ProviderPayablesContext.Provider value={value}>{children}</ProviderPayablesContext.Provider>;
};

export const useProviderPayables = (): ProviderPayablesContextValue => {
	const context = useContext(ProviderPayablesContext);
	if (!context) {
		throw new Error('useProviderPayables debe usarse dentro de ProviderPayablesProvider');
	}
	return context;
};
